import { ActivatedRoute } from '@angular/router'
import { DirectusFiles, Game, Term } from 'src/types'

export enum GameMode {
  PlayGame = 'Play Game',
  BuildGame = 'Build Game'
}

export enum GameTemplateMode {
  Create = 'create',
  Update = 'update'
}

export enum GameType {
  SideActivity = 'Side Activity',
  SceneDragAndDrop = 'Scene Drag And Drop',
  BodyParts = 'Body Parts',
  BodyPartsPrefilled = 'Body Parts Prefilled',
  Sentence = 'Sentence',
  StoryFill = 'Story Fill',
  DragImage = 'Drag Image',
  MatchPairs = 'Match Pairs',
  MatchOriginalTermWithImage = 'Match Original Term With Image',
  SelectItem = 'Select Item',
  SelectWord = 'Select Word',
  SelectWordForImage = 'Select Word For Image',
  SelectImageForWord = 'Select Image For Word',
}

export const GameTypeEnumMap: Record<string, string> = {
  [GameType.SideActivity]: 'game/side-activity/',
  [GameType.SceneDragAndDrop]: 'game/activity-scene-drag-and-drop/',
  [GameType.BodyParts]: 'game/activity-body-parts/',
  [GameType.BodyPartsPrefilled]: 'game/activity-body-parts-prefilled/',
  [GameType.Sentence]: 'game/activity-reading-listening/',
  [GameType.StoryFill]: 'game/activity-story-fill/',
  [GameType.DragImage]: 'game/activity-drag-image/',
  [GameType.MatchPairs]: 'game/activity-match-pairs/',
  [GameType.MatchOriginalTermWithImage]: 'game/activity-match-original-term-with-image/',
  [GameType.SelectItem]: 'game/activity-select-item/',
  [GameType.SelectWord]: 'game/activity-select-word/',
  [GameType.SelectImageForWord]: 'game/activity-select-image-for-word/',
  [GameType.SelectWordForImage]: 'game/activity-select-word-for-image/',
}

export interface UserGame extends Game {
  failed?: boolean
}

export interface MappedTerms {
  answerTerms: UserGameTerm[]
  terms: UserGameTerm[]
}

export interface UserGameTerm extends Term {
  english_sound?: string | DirectusFiles | null
  isMissing?: boolean
}

export interface GameData {
  Info: string | BodyPartsInfo | StoryFillInfo | SideActivityInfo
}

export interface Answer {
  translated?: string
  original?: string
  image?: string | null
  image_coloured?: string | DirectusFiles | null
  id?: string
  colorState?: string | null
  isCorrect: boolean
  sound?: string | DirectusFiles | null
  english_sound?: string | DirectusFiles | null
  isMissing?: boolean
}

export interface SideActivityImages {
  name: string
  terms?: string[]
}

export interface SideActivityInfo {
  images?: SideActivityImages[]
  transcription?: TranscriptionData[]
  title?: string
  description?: string
}

export interface BodyPartsInfo {
  image: string
  leftContainers: number
  rightContainers: number
  leftContainersTerms?: string[]
  rightContainersTerms?: string[],
  bodyParts?: string[]
}

export interface StoryFillInfo {
  images?: string[]
  sentence?: string
  sentenceWords: string[]
}

export enum BodyPartsContainerType {
  LeftTop = 'Left Top',
  LeftMiddle = 'Left Middle',
  LeftBottom = 'Left Bottom',
  RightTop = 'Right Top',
  RightMiddle = 'Right Middle',
  RightBottom = 'Right Bottom'
}

export enum LearningEventType {
  GameSucceeded = 'Game Succeeded',
  GameFailed = 'Game Failed',
  WordCollected = 'Word Collected',
  LevelCompleted = 'Level Completed',
  LevelCompletedWithoutMistakes = 'Level Completed Without Mistakes',
  UnitCompleted = 'Unit Completed',
  SideUnitCompleted = 'Side Unit Completed',
  PlayedGame = 'Played Game',
  LoggedIn = 'Logged In',
  GameAssigned = 'Game Assigned',
  CollectableFound = 'Collectable Found'
}

export enum GameSentenceType {
  Line1 = 'Line1',
  Line2 = 'Line2',
  Line3 = 'Line3',
  WordPool = 'WordPool'
}

export enum SideActivityType {
  AudioSong = 'Audio Song',
  AudioImageWithLabels = 'Audio Image With Labels',
  Video = 'Video',
  CollectItem = 'Collect Item'
}

export interface TranscriptionData {
  word: string
  start: number
  end: number
}

export interface NextGameData {
  game: UserGame
  games: UserGame[]
  collectedWords: number
  mistakes: number
  route?: ActivatedRoute
  gamesInSequence: number
  wordRating: number
}

export interface HelpText {
  title: string
  body: string
}

export const GameHelpText: Record<string, HelpText> = {
  [GameType.SideActivity]: {
    title: `Let's All Sing`,
    body: 'Listen to the Dharawal song by pressing play. Follow along as the words are highlighted in time with the song. Listen to the song all the way through to successfully finish this activity.'
  },
  [GameType.BodyParts]: {
    title: 'Body Parts',
    body: 'The objective of this game is to name each of the body parts correctly in Dharawal. Placing a word in a box will colour in the corresponding body part. The game is complete once you have all available words in a box.'
  },
  [GameType.BodyPartsPrefilled]: {
    title: 'Body Parts',
    body: 'The objective of this game is to name each of the body parts correctly in Dharawal. Placing a word in a box will colour in the corresponding body part. The game is complete once you have all available words in a box.'
  },
  [GameType.Sentence]: {
    title: 'Reading and Listening',
    body: 'Read or listen to the phrase and then translate it into English. Drag the Dharawal words onto the lines to construct your sentence.'
  },
  [GameType.StoryFill]: {
    title: 'Storyfill',
    body: 'Find the missing Dharawal word to complete the sentence. You will see a selection of words to choose from to drag over onto the sentence.'
  },
  [GameType.DragImage]: {
    title: 'Drag and Drop',
    body: 'You will need to drag the correct object onto the Dharawal word featured in the centre of the screen.'
  },
  [GameType.MatchPairs]: {
    title: 'Word Match',
    body: 'You are presented with two columns of words - One in Dharawal and the other in English. Match the word in the left column to its counterpart in the right column.'
  },
  [GameType.MatchOriginalTermWithImage]: {
    title: 'Image Match',
    body: 'Match the Dharawal word with its corresponding image. Select a word and then an image to match.'
  },
  [GameType.SelectItem]: {
    title: 'Image Match',
    body: 'Select the card (containing a word and supporting image) that is the correct translation of the displayed word.'
  },
  [GameType.SelectWord]: {
    title: 'Word Match',
    body: 'Select the card (containing a word) that is the correct translation of the displayed word.'
  },
  [GameType.SelectImageForWord]: {
    title: 'Image Match',
    body: 'Select the card (containing supporting image) that is the correct translation of the displayed word.'
  },
  [GameType.SelectWordForImage]: {
    title: 'Word Match',
    body: 'Select the card (containing a word) that is the correct translation of the displayed image.'
  },
}

export enum ActivityType {
  Game = 'Game',
  SideActivity = 'SideActivity'
}
